import { z } from 'zod';
// ─── Experiment ──────────────────────────────────────────────────────────────
//
// A refinement-agent decision that a one-off Workflow is worth creating and
// evaluating. Ties together:
//
//   - a refinement chat thread (opaque foreign id into theopolis)
//   - the parent Workflow (FK to `workflow.id` at the DB layer)
//   - a one-off experimental `WorkflowVersion` (FK to `workflow_version.id`)
//
// The "evaluation for an experiment" is NOT persisted on the experiment row.
// It's derived live by `GET /v1/experiments/:id/comparison`:
//   candidate = most recent Evaluation whose Runs target the experimental
//               `workflowVersionId`
//   baseline  = most recent Evaluation whose Runs target the workflow's
//               `currentVersionId`
//
// All reads at the API boundary filter `deletedAt: null`. `deletedAt` is
// NOT exposed on the wire — soft-delete is an internal mechanism. The
// `DELETE /v1/experiments/:id` endpoint sets it to `now()`.
export const ExperimentStatusSchema = z.enum([
    'PENDING',
    'RUNNING',
    'FAILED',
    'WAITING_FOR_HUMAN_INPUT',
    'HUMAN_ACCEPTED',
    'HUMAN_REJECTED',
]);
/**
 * AI grader's verdict on whether the experiment's
 * `hypothesisOfEffectOfChange` was met by the eval results. Orthogonal
 * to `ExperimentStatus`: the status tracks the human-review lifecycle,
 * the verdict tracks the AI grader's outcome.
 *
 *   PENDING      → no grader has run yet (default at creation)
 *   SATISFIED    → AI grader confirmed the hypothesis; the experiment
 *                  is promoted to the workflow's "Suggestions" section
 *                  in the refinement console
 *   UNSATISFIED  → AI grader rejected the hypothesis
 */
export const ExperimentHypothesisVerdictSchema = z.enum(['PENDING', 'SATISFIED', 'UNSATISFIED']);
export const ExperimentSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    userId: z.string(),
    /** Opaque foreign id into theopolis `chat_thread.id`. */
    threadId: z.string(),
    workflowId: z.string(),
    /** Denormalized parent workflow name. Sourced from `workflow.name` at
     *  read time; lets the refinement overview page group experiments by
     *  workflow without a second round-trip. */
    workflowName: z.string(),
    /** FK to the experimental one-off `WorkflowVersion.id` the refinement
     *  agent created and evaluated. Pins the experiment to the exact
     *  graph snapshot under test (distinct from the workflow's current
     *  production version). */
    workflowVersionId: z.string(),
    name: z.string(),
    /** Free-form description of what the experiment is doing. Optional —
     *  older experiments may not carry one. */
    description: z.string().nullable(),
    /** Agent's prediction of how the workflow change will affect outcomes.
     *  Optional for the same reason. */
    hypothesisOfEffectOfChange: z.string().nullable(),
    status: ExperimentStatusSchema,
    /** AI grader's verdict. PENDING until a grader runs; SATISFIED →
     *  the experiment is surfaced as a "Suggestion" under the workflow. */
    hypothesisSatisfied: ExperimentHypothesisVerdictSchema,
    /** AI grader's free-form reasoning behind `hypothesisSatisfied` —
     *  eval deltas, numeric evidence, qualitative observations. Surfaced
     *  in the detail modal only when the verdict is not PENDING. */
    reasoningForHypothesisJudgement: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// ─── Comparison ──────────────────────────────────────────────────────────────
//
// One row per side (candidate, baseline). Metrics are derived live:
//
//   accuracy      ← Legacy GT: `scoreSummary.passed / .total` (0-1)
//                   Task-based: (tasks where passedTrials === nTrials) / totalTasks
//   costUsd       ← `SUM(CostRecord.usd)` over this eval's runs on this
//                   version (TOTAL workflow-run cost; string for precision,
//                   matching the existing CostRecord wire convention)
//   runCount      ← number of Runs the eval executed on this version
//   avgCostPerRunUsd ← `costUsd / runCount` — the NORMALIZED figure. This is the
//                   metric the comparison delta and the refinement optimizer
//                   judge against, so a bigger ground-truth set doesn't make a
//                   candidate look more expensive just for running more cases.
//   speedMs       ← `Evaluation.finishedAt − Evaluation.startedAt`
//
// `costUsd` (total) is retained for budget visibility and SDK back-compat;
// `avgCostPerRunUsd` is the additive per-run metric. Any of these is null when
// the underlying data hasn't been produced yet (eval still running, no cost
// records emitted, etc.). The side itself is null when no evaluation has run
// yet against that workflow version.
export const ExperimentComparisonSideSchema = z.object({
    evaluationId: z.string(),
    workflowVersionId: z.string(),
    status: z.string(),
    accuracy: z.number().nullable(),
    /** TOTAL workflow-run cost: `SUM(CostRecord.usd)` over this eval's runs on
     *  this version. Retained for budget visibility / SDK back-compat. */
    costUsd: z.string().nullable(),
    /** Number of Runs this eval executed on this version — the denominator for
     *  `avgCostPerRunUsd`. Null when unknown. */
    runCount: z.number().int().nullable(),
    /** `costUsd / runCount` — average workflow-run cost per run. The figure the
     *  comparison delta and the refinement optimizer judge against (the
     *  optimizer's `targetCost` is defined as "USD per run"). Normalizes out
     *  dataset size. String for precision; null when no cost records exist or
     *  `runCount` is 0. */
    avgCostPerRunUsd: z.string().nullable(),
    /** TOTAL eval wall-clock: `Evaluation.finishedAt − startedAt` (ms). Retained
     *  for "time to results" visibility / SDK back-compat. NOTE: confounded by
     *  dataset size AND worker concurrency, so it is NOT the comparison signal. */
    speedMs: z.number().int().nullable(),
    /** Mean per-run speed (avg run duration): average of `Run.finishedAt −
     *  startedAt` over the eval's runs on this version (ms, rounded). The NORMALIZED speed metric the
     *  comparison delta and the refinement optimizer judge against (matching the
     *  preference's per-run `targetSpeed`) — independent of dataset size and
     *  worker concurrency. Null when no run has both timestamps. */
    avgSpeedMsPerRun: z.number().int().nullable(),
    startedAt: z.date().nullable(),
    finishedAt: z.date().nullable(),
    createdAt: z.date(),
});
/**
 * Deltas between candidate and baseline. Each field is the relative
 * percentage change `(candidate - baseline) / baseline * 100`. Sign
 * convention:
 *
 *   accuracyPct  positive ⇒ candidate more accurate (better)
 *   costPct      positive ⇒ candidate more expensive PER RUN (worse)
 *   speedPct     positive ⇒ candidate slower PER RUN         (worse)
 *
 * `costPct` is computed from each side's `avgCostPerRunUsd` and `speedPct` from
 * each side's `avgSpeedMsPerRun` (NOT the raw `costUsd` / `speedMs` totals),
 * so a candidate evaluated over a larger dataset is not reported as more
 * expensive or slower purely for running more cases. Each delta is
 * independently null when either side lacks the underlying metric, or when
 * the baseline value is zero (division undefined).
 */
export const ExperimentComparisonDeltasSchema = z.object({
    accuracyPct: z.number().nullable(),
    costPct: z.number().nullable(),
    speedPct: z.number().nullable(),
});
/** Per-Task pass-rate comparison between candidate and baseline. Only present
 *  when both sides used the Task-based eval pipeline (scoreSummary contains
 *  `taskResults`). Each entry covers one Task that appears in BOTH sides. */
export const ExperimentTaskDeltaSchema = z.object({
    taskId: z.string(),
    taskName: z.string(),
    candidatePassRate: z.number(),
    baselinePassRate: z.number(),
    /** Relative percentage change: `(candidate - baseline) / baseline * 100`.
     *  Positive = candidate better. Null when baseline is 0. */
    deltaPct: z.number().nullable(),
});
export const ExperimentComparisonSchema = z.object({
    experimentId: z.string(),
    /** Most recent Evaluation whose Runs target the experimental
     *  `workflowVersionId`. Null when no eval has run yet. */
    candidate: ExperimentComparisonSideSchema.nullable(),
    /** Most recent Evaluation whose Runs target the workflow's
     *  `currentVersionId`. Null when the workflow has no current version,
     *  when the candidate IS the current version (degenerate), or when no
     *  eval has ever run against current. */
    baseline: ExperimentComparisonSideSchema.nullable(),
    deltas: ExperimentComparisonDeltasSchema,
    /** Per-Task pass-rate comparison. Only present when BOTH sides used the
     *  Task-based pipeline. Each entry = one Task that appears in both evals. */
    taskDeltas: z.array(ExperimentTaskDeltaSchema).optional(),
    /** Response-level signals (e.g. "candidate eval still running", "no
     *  baseline because workflow has no active version"). The UI renders
     *  these as a muted banner above the metrics. */
    warnings: z.array(z.string()),
});
// ─── Monthly optimization spend ───────────────────────────────────────────────
//
// Total cost incurred in the CURRENT calendar month (UTC) by the optimization
// loop. Two slices, kept separate so each can be sourced independently and the
// console can show a breakdown:
//
//   evaluationCostUsd — `SUM(CostRecord.usd)` (this month) over the Runs of
//                       Evaluations on any WORKFLOW that has at least one
//                       (non-deleted) Experiment — counting BOTH the candidate
//                       (experimental version) and baseline (current version)
//                       sides of the comparison. Owned by apps/api (Prisma).
//   sessionCostUsd    — cost of the refinement/optimization chat SESSIONS tied
//                       to those experiments. Lives in theopolis (`cost_record`),
//                       reached over HTTP. `null` until that slice is wired in —
//                       distinct from `0` (wired, but nothing spent).
//
// `totalCostUsd` is the sum of the slices that are currently available
// (`sessionCostUsd` contributes 0 while it is null). All amounts are USD,
// returned as numbers (the monthly aggregate doesn't need sub-cent string
// precision the per-record wire format uses).
/** One workflow's slice of this month's evaluation spend, used to draw the
 *  per-workflow cost breakdown (a horizontal stacked bar) under the limit. */
export const ExperimentWorkflowSpendSchema = z.object({
    /** Workflow the spend is attributed to. */
    workflowId: z.string(),
    /** Display name at query time (workflows can be renamed; this is a snapshot). */
    workflowName: z.string(),
    /** `SUM(CostRecord.usd)` this month over this workflow's evaluation runs. */
    evaluationCostUsd: z.number(),
});
export const ExperimentMonthlySpendSchema = z.object({
    /** Calendar month the figures cover, `YYYY-MM` in UTC. */
    month: z.string(),
    /** Inclusive start of the month window (ISO 8601, UTC). */
    periodStart: z.string(),
    /** Exclusive end of the month window — first instant of next month (ISO 8601, UTC). */
    periodEnd: z.string(),
    currency: z.literal('USD'),
    /** Evaluation cost this month for experimental workflow versions (apps/api slice). */
    evaluationCostUsd: z.number(),
    /** Optimization-session cost this month (theopolis slice). `null` until wired. */
    sessionCostUsd: z.number().nullable(),
    /** Sum of the available slices (session counts as 0 while null). */
    totalCostUsd: z.number(),
    /** Per-workflow split of `evaluationCostUsd`, ordered by spend descending.
     *  Only workflows with non-zero spend this month appear. Powers the
     *  cost-by-workflow breakdown bar in the console. */
    byWorkflow: z.array(ExperimentWorkflowSpendSchema),
});
// ─── Request bodies ──────────────────────────────────────────────────────────
export const CreateExperimentInputSchema = z.object({
    threadId: z.string().min(1),
    /** FK-validated against `workflow.id`; cross-org rejected with 404. */
    workflowId: z.string().min(1),
    /** FK-validated against `workflow_version.id`; must belong to `workflowId`. */
    workflowVersionId: z.string().min(1),
    name: z.string().min(1),
    /** Optional free-form description; the refinement agent populates it
     *  when it has a useful summary to surface. */
    description: z.string().nullable().optional(),
    /** Optional agent prediction of effect. Read by the AI grader. */
    hypothesisOfEffectOfChange: z.string().nullable().optional(),
    /** Optional pre-populated grader reasoning. Most callers leave this
     *  null at create time and PATCH it later alongside `hypothesisSatisfied`. */
    reasoningForHypothesisJudgement: z.string().nullable().optional(),
});
export const UpdateExperimentInputSchema = z
    .object({
    name: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    hypothesisOfEffectOfChange: z.string().nullable().optional(),
    status: ExperimentStatusSchema.optional(),
    /** PATCHable by the AI grader (and human reviewers if they override
     *  it). PENDING is the default; SATISFIED promotes the experiment to
     *  the workflow's Suggestions section. */
    hypothesisSatisfied: ExperimentHypothesisVerdictSchema.optional(),
    /** AI grader's reasoning behind the verdict. Typically PATCHed in the
     *  same call that sets `hypothesisSatisfied`. */
    reasoningForHypothesisJudgement: z.string().nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided',
});
//# sourceMappingURL=experiment.js.map